import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle, DialogHeader } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Users, Plus, Copy, Trash2, Loader2, DollarSign, Store,
  TrendingUp, CheckCircle2, Edit2, Link2, ExternalLink, Wallet
} from "lucide-react";

export function InfluencerTab({ authAxios }) {
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxiosRef.current.get("/influencers");
      setInfluencers(res.data);
    } catch { toast.error("Erro ao carregar influenciadores"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const copyLink = (inf) => {
    const base = window.location.origin;
    const slug = inf.link || `/m/${inf.restaurant_id}?inf=${inf.code}`;
    navigator.clipboard.writeText(`${base}${slug}`);
    toast.success(`Link do ${inf.name} copiado!`);
  };

  const deleteInf = async (id, name) => {
    if (!window.confirm(`Remover influenciador "${name}"?`)) return;
    try {
      await authAxios.delete(`/influencers/${id}`);
      toast.success("Removido");
      fetch();
    } catch { toast.error("Erro"); }
  };

  const payInf = async (inf) => {
    const amount = inf.pending_commission;
    if (amount <= 0) return;
    try {
      await authAxios.post(`/influencers/${inf.id}/pay`, { amount });
      toast.success(`${amount.toFixed(2)} EUR pago para ${inf.name}`);
      fetch();
    } catch { toast.error("Erro ao pagar"); }
  };

  const totals = {
    orders: influencers.reduce((s, i) => s + (i.total_orders || 0), 0),
    sales: influencers.reduce((s, i) => s + (i.total_sales || 0), 0),
    commission: influencers.reduce((s, i) => s + (i.total_commission || 0), 0),
    pending: influencers.reduce((s, i) => s + Math.max(0, i.pending_commission || 0), 0),
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { icon: Users, label: "Influenciadores", value: influencers.length, color: "text-blue-600 bg-blue-50" },
          { icon: Store, label: "Pedidos", value: totals.orders, color: "text-emerald-600 bg-emerald-50" },
          { icon: DollarSign, label: "Vendas", value: `${totals.sales.toFixed(2)} EUR`, color: "text-violet-600 bg-violet-50" },
          { icon: Wallet, label: "Comissoes Pendentes", value: `${totals.pending.toFixed(2)} EUR`, color: "text-amber-600 bg-amber-50" },
        ].map((s, i) => (
          <Card key={i} data-testid={`inf-stat-${i}`}>
            <CardContent className="p-4">
              <div className={`w-9 h-9 rounded-xl ${s.color.split(" ")[1]} flex items-center justify-center mb-2`}>
                <s.icon className={`w-5 h-5 ${s.color.split(" ")[0]}`} />
              </div>
              <p className="text-lg font-bold">{s.value}</p>
              <p className="text-[10px] text-muted-foreground">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Header + Add */}
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-lg">Influenciadores</h2>
        <Button onClick={() => { setEditingId(null); setShowCreate(true); }} className="gap-1.5 bg-orange-500 hover:bg-orange-600" data-testid="add-influencer-btn">
          <Plus className="w-4 h-4" /> Novo Influenciador
        </Button>
      </div>

      {/* List */}
      {influencers.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p className="font-medium">Nenhum influenciador cadastrado</p>
            <p className="text-xs mt-1">Crie influenciadores para promover seu restaurante e rastrear vendas</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {influencers.map(inf => (
            <Card key={inf.id} data-testid={`influencer-card-${inf.code}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {inf.name?.charAt(0)?.toUpperCase() || "I"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-bold text-sm">{inf.name}</p>
                      <Badge className={`text-[9px] ${inf.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {inf.status === "active" ? "Ativo" : "Inativo"}
                      </Badge>
                      <Badge variant="outline" className="text-[9px] font-mono">{inf.code}</Badge>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                      {inf.phone && <span>{inf.phone}</span>}
                      {inf.email && <span>{inf.email}</span>}
                      <span className="font-medium text-orange-600">
                        {inf.commission_type === "fixed" ? `${inf.commission_value} EUR/pedido` : `${inf.commission_value}% das vendas`}
                      </span>
                    </div>
                    {/* Link */}
                    <div className="flex items-center gap-1.5 mt-2">
                      <div className="flex-1 bg-gray-50 rounded-lg px-2.5 py-1.5 text-[10px] font-mono text-gray-500 truncate border">
                        {window.location.origin}{inf.link || `/m/...?inf=${inf.code}`}
                      </div>
                      <Button size="sm" variant="outline" className="h-7 px-2 text-[10px] gap-1" onClick={() => copyLink(inf)} data-testid={`copy-link-${inf.code}`}>
                        <Copy className="w-3 h-3" /> Copiar
                      </Button>
                    </div>
                  </div>
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-3 text-center flex-shrink-0">
                    <div><p className="text-sm font-bold">{inf.total_orders || 0}</p><p className="text-[9px] text-muted-foreground">Pedidos</p></div>
                    <div><p className="text-sm font-bold text-emerald-600">{(inf.total_sales || 0).toFixed(2)}</p><p className="text-[9px] text-muted-foreground">Vendas</p></div>
                    <div><p className="text-sm font-bold text-amber-600">{Math.max(0, inf.pending_commission || 0).toFixed(2)}</p><p className="text-[9px] text-muted-foreground">Pendente</p></div>
                  </div>
                </div>
                {/* Actions */}
                <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[10px] gap-1" onClick={() => { setEditingId(inf.id); setShowCreate(true); }} data-testid={`edit-inf-${inf.code}`}>
                    <Edit2 className="w-3 h-3" /> Editar
                  </Button>
                  {(inf.pending_commission || 0) > 0 && (
                    <Button size="sm" className="h-7 px-2 text-[10px] gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => payInf(inf)} data-testid={`pay-inf-${inf.code}`}>
                      <Wallet className="w-3 h-3" /> Pagar {inf.pending_commission?.toFixed(2)} EUR
                    </Button>
                  )}
                  <div className="flex-1" />
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[10px] text-red-500 hover:bg-red-50 gap-1" onClick={() => deleteInf(inf.id, inf.name)} data-testid={`delete-inf-${inf.code}`}>
                    <Trash2 className="w-3 h-3" /> Remover
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <InfluencerDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        authAxios={authAxios}
        editId={editingId}
        influencers={influencers}
        onSaved={() => { setShowCreate(false); fetch(); }}
      />
    </div>
  );
}

function InfluencerDialog({ open, onClose, authAxios, editId, influencers, onSaved }) {
  const existing = editId ? influencers.find(i => i.id === editId) : null;
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    commission_type: "percentage", commission_value: 5, pix_or_iban: "",
  });
  const [saving, setSaving] = useState(false);
  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (existing) {
      setForm({
        name: existing.name || "",
        phone: existing.phone || "",
        email: existing.email || "",
        commission_type: existing.commission_type || "percentage",
        commission_value: existing.commission_value || 5,
        pix_or_iban: existing.pix_or_iban || "",
      });
    } else {
      setForm({ name: "", phone: "", email: "", commission_type: "percentage", commission_value: 5, pix_or_iban: "" });
    }
  }, [existing, open]);

  const save = async () => {
    if (!form.name) { toast.error("Nome obrigatorio"); return; }
    setSaving(true);
    try {
      if (editId) {
        await authAxios.patch(`/influencers/${editId}`, form);
        toast.success("Influenciador atualizado");
      } else {
        await authAxios.post("/influencers", form);
        toast.success("Influenciador criado!");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="influencer-dialog">
        <DialogHeader>
          <DialogTitle>{editId ? "Editar Influenciador" : "Novo Influenciador"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Nome *</Label>
            <Input value={form.name} onChange={e => u("name", e.target.value)} placeholder="Nome do influenciador" data-testid="inf-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Telefone</Label>
              <Input value={form.phone} onChange={e => u("phone", e.target.value)} placeholder="+34 600 000 000" data-testid="inf-phone-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <Input value={form.email} onChange={e => u("email", e.target.value)} placeholder="email@..." data-testid="inf-email-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold">Comissao</Label>
            <div className="flex items-center gap-3">
              <select
                value={form.commission_type}
                onChange={e => u("commission_type", e.target.value)}
                className="text-sm border rounded-md px-3 py-2 bg-white"
                data-testid="inf-commission-type"
              >
                <option value="percentage">Porcentagem (%)</option>
                <option value="fixed">Valor Fixo (EUR)</option>
              </select>
              <div className="flex items-center gap-1.5">
                <Input
                  type="number"
                  step={form.commission_type === "fixed" ? "1" : "0.5"}
                  min="0"
                  value={form.commission_value}
                  onChange={e => u("commission_value", parseFloat(e.target.value) || 0)}
                  className="w-24"
                  data-testid="inf-commission-value"
                />
                <span className="text-sm text-muted-foreground">{form.commission_type === "fixed" ? "EUR/pedido" : "%"}</span>
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground">
              {form.commission_type === "fixed"
                ? `O influenciador ganha ${form.commission_value} EUR por cada pedido`
                : `O influenciador ganha ${form.commission_value}% do valor de cada venda`
              }
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">PIX / IBAN (para pagamento)</Label>
            <Input value={form.pix_or_iban} onChange={e => u("pix_or_iban", e.target.value)} placeholder="ES12 3456..." data-testid="inf-iban-input" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onClose(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white gap-1" data-testid="inf-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {editId ? "Salvar" : "Criar Influenciador"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
